import { useId } from 'react'
import type { DiagramId } from './types'

/**
 * Inline SVG diagrams for the Help centre. Each one is a labelled image (title + description)
 * followed by the same information in words, so the picture is never the only carrier.
 * Colours come from the theme through Tailwind fill/stroke utilities.
 */

const NODE = 'fill-surface stroke-teal-300'
const NODE_STRONG = 'fill-teal stroke-teal'
const NODE_SOFT = 'fill-wash stroke-line-strong'
const LABEL = 'fill-ink font-display text-[14px] font-semibold'
const LABEL_ON_STRONG = 'fill-white font-display text-[14px] font-semibold'
const SMALL = 'fill-sage text-[11.5px]'
const EDGE = 'stroke-teal-500'

interface NodeProps {
  x: number
  y: number
  w: number
  label: string
  tone?: 'plain' | 'strong' | 'soft'
}

function Node({ x, y, w, label, tone = 'plain' }: NodeProps) {
  const box = tone === 'strong' ? NODE_STRONG : tone === 'soft' ? NODE_SOFT : NODE
  return (
    <g>
      <rect x={x} y={y} width={w} height={44} rx={10} className={box} strokeWidth={1.5} />
      <text
        x={x + w / 2}
        y={y + 27}
        textAnchor="middle"
        className={tone === 'strong' ? LABEL_ON_STRONG : LABEL}
      >
        {label}
      </text>
    </g>
  )
}

function Arrow({ d, marker, dashed = false }: { d: string; marker: string; dashed?: boolean }) {
  return (
    <path
      d={d}
      fill="none"
      className={EDGE}
      strokeWidth={1.75}
      strokeDasharray={dashed ? '5 4' : undefined}
      markerEnd={`url(#${marker})`}
    />
  )
}

function Defs({ marker }: { marker: string }) {
  return (
    <defs>
      <marker
        id={marker}
        viewBox="0 0 10 10"
        refX="9"
        refY="5"
        markerWidth="7"
        markerHeight="7"
        orient="auto-start-reverse"
      >
        <path d="M 0 0 L 10 5 L 0 10 z" className="fill-teal-500" />
      </marker>
    </defs>
  )
}

function Frame({
  title,
  desc,
  viewBox,
  minWidth,
  children,
  inWords,
}: {
  title: string
  desc: string
  viewBox: string
  minWidth: string
  children: React.ReactNode
  inWords: string[]
}) {
  const id = useId()
  return (
    <figure className="help-diagram rounded-lg border border-line bg-surface p-3 sm:p-4">
      <div className="overflow-x-auto">
        <svg
          viewBox={viewBox}
          role="img"
          aria-labelledby={`${id}-t`}
          aria-describedby={`${id}-d`}
          className="h-auto w-full min-w-(--diagram-min)"
          style={{ '--diagram-min': minWidth } as React.CSSProperties}
        >
          <title id={`${id}-t`}>{title}</title>
          <desc id={`${id}-d`}>{desc}</desc>
          {children}
        </svg>
      </div>
      <figcaption className="mt-2 text-xs leading-relaxed text-sage">
        <span className="font-semibold text-ink-soft">In words: </span>
        {inWords.join(' ')}
      </figcaption>
    </figure>
  )
}

function ReviewFlow() {
  const marker = useId().replace(/:/g, '')
  return (
    <Frame
      title="The review and release flow"
      desc="Draft, then in review, then approved, then published. A return for changes sends a record back to draft; a retraction withdraws a published record. Investors see nothing before publication."
      viewBox="0 0 760 236"
      minWidth="40rem"
      inWords={[
        'A draft is submitted for review, approved by an independent reviewer and then published.',
        'Returning it for changes sends it back to draft; retracting withdraws a published version.',
        'Investors see a record only once it is published.',
      ]}
    >
      <Defs marker={marker} />
      {/* visibility divide */}
      <line
        x1={470}
        y1={14}
        x2={470}
        y2={222}
        className="stroke-line-strong"
        strokeDasharray="4 4"
      />
      <text x={462} y={26} textAnchor="end" className={SMALL}>
        Internal – not visible to investors
      </text>
      <text x={478} y={26} className={SMALL}>
        Released to investors
      </text>

      <Node x={16} y={64} w={120} label="Draft" />
      <Node x={176} y={64} w={130} label="In review" />
      <Node x={346} y={64} w={110} label="Approved" />
      <Node x={496} y={64} w={130} label="Published" tone="strong" />
      <Node x={496} y={168} w={130} label="Retracted" tone="soft" />

      <Arrow d="M 136 86 L 174 86" marker={marker} />
      <text x={155} y={56} textAnchor="middle" className={SMALL}>
        Submit
      </text>
      <Arrow d="M 306 86 L 344 86" marker={marker} />
      <text x={325} y={56} textAnchor="middle" className={SMALL}>
        Approve
      </text>
      <Arrow d="M 456 86 L 494 86" marker={marker} />
      <text x={475} y={56} textAnchor="middle" className={SMALL}>
        Publish
      </text>
      {/* return loop */}
      <Arrow d="M 401 108 C 401 150, 76 150, 76 110" marker={marker} dashed />
      <text x={238} y={170} textAnchor="middle" className={SMALL}>
        Return for changes (clears the approval)
      </text>
      {/* retract */}
      <Arrow d="M 561 108 L 561 166" marker={marker} dashed />
      <text x={572} y={142} className={SMALL}>
        Retract
      </text>
      <text x={640} y={86} className={SMALL}>
        Correct →
      </text>
      <text x={640} y={102} className={SMALL}>
        new draft
      </text>
    </Frame>
  )
}

function ParticipationStates() {
  const marker = useId().replace(/:/g, '')
  return (
    <Frame
      title="Participation request states"
      desc="Requested, then acknowledged, then awaiting company consent, then confirmed. From any open state the manager can decline, with a reason, or the investor can withdraw."
      viewBox="0 0 760 200"
      minWidth="40rem"
      inWords={[
        'A request starts as Requested. The engagement manager acknowledges it, may mark it as awaiting company consent, and confirms it.',
        'From any open state the manager can decline it, with a reason, or the investor can withdraw it.',
      ]}
    >
      <Defs marker={marker} />
      <Node x={16} y={40} w={124} label="Requested" />
      <Node x={180} y={40} w={140} label="Acknowledged" />
      <Node x={360} y={40} w={200} label="Awaiting company consent" />
      <Node x={600} y={40} w={144} label="Confirmed" tone="strong" />
      <Arrow d="M 140 62 L 178 62" marker={marker} />
      <Arrow d="M 320 62 L 358 62" marker={marker} />
      <Arrow d="M 560 62 L 598 62" marker={marker} />
      <text x={380} y={30} textAnchor="middle" className={SMALL}>
        The engagement manager moves a request forward, each time with a note
      </text>
      {/* any open state bracket */}
      <path
        d="M 78 92 L 78 112 L 672 112 L 672 92"
        fill="none"
        className="stroke-line-strong"
        strokeDasharray="4 4"
      />
      <text x={375} y={130} textAnchor="middle" className={SMALL}>
        from any open state
      </text>
      <Arrow d="M 250 112 L 250 146" marker={marker} dashed />
      <Arrow d="M 500 112 L 500 146" marker={marker} dashed />
      <Node x={176} y={148} w={148} label="Declined" tone="soft" />
      <Node x={426} y={148} w={148} label="Withdrawn" tone="soft" />
      <text x={250} y={190} textAnchor="middle" className={SMALL}>
        by the manager, with a reason
      </text>
      <text x={500} y={190} textAnchor="middle" className={SMALL}>
        by the investor
      </text>
    </Frame>
  )
}

function StageLadder() {
  const steps = [
    ['S0', 'Baseline', 'The starting position is recorded'],
    ['S1', 'Dialogue established', 'Substantive dialogue on the objective'],
    ['S2', 'Commitment recorded', 'A specific written or public commitment'],
    ['S3', 'Implementation evidenced', 'Independent evidence of practice'],
    ['S4', 'Outcome evidenced', 'Independent evidence of the result'],
  ] as const
  const fills = ['fill-teal-100', 'fill-teal-200', 'fill-teal-300', 'fill-teal-500', 'fill-teal']
  const base = 214
  return (
    <Frame
      title="The five-stage ladder"
      desc="Five ascending steps: S0 Baseline, S1 Dialogue established, S2 Commitment recorded, S3 Implementation evidenced, S4 Outcome evidenced."
      viewBox="0 0 760 236"
      minWidth="40rem"
      inWords={[
        'S0 Baseline: the starting position is recorded.',
        'S1 Dialogue established: the company engages substantively.',
        'S2 Commitment recorded: a specific written or public commitment.',
        'S3 Implementation evidenced: independent evidence shows it in practice.',
        'S4 Outcome evidenced: independent evidence shows the intended result.',
        'Only a published, independently reviewed evidence review moves a stage.',
      ]}
    >
      {steps.map(([code, name], index) => {
        const x = 16 + index * 148
        const h = 40 + index * 28
        const top = base - h
        const dark = index >= 3
        const [first, ...rest] = name.split(' ')
        return (
          <g key={code}>
            <rect
              x={x}
              y={top}
              width={140}
              height={h}
              rx={8}
              className={`${fills[index]} stroke-teal-300`}
              strokeWidth={1}
            />
            <text
              x={x + 12}
              y={top + 25}
              className={`font-display text-[15px] font-semibold ${dark ? 'fill-white' : 'fill-teal-800'}`}
            >
              {code}
            </text>
            <text
              x={x + 12}
              y={top - 26}
              className="fill-ink font-display text-[12.5px] font-semibold"
            >
              {first}
            </text>
            {rest.length > 0 ? (
              <text
                x={x + 12}
                y={top - 10}
                className="fill-ink font-display text-[12.5px] font-semibold"
              >
                {rest.join(' ')}
              </text>
            ) : null}
          </g>
        )
      })}
      <line x1={16} y1={base + 1} x2={744} y2={base + 1} className="stroke-line-strong" />
      <text x={16} y={230} className={SMALL}>
        Only a published, independently reviewed evidence review moves an objective up a stage.
      </text>
    </Frame>
  )
}

export function Diagram({ id }: { id: DiagramId }) {
  switch (id) {
    case 'review-flow':
      return <ReviewFlow />
    case 'participation-states':
      return <ParticipationStates />
    case 'stage-ladder':
      return <StageLadder />
  }
}
